import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { Tag } from "@/api/tags"
import type { BackendBillingPlanName } from "@/billing/billing"
import type {
  Pagination,
  Project,
  ProjectIdParam,
  ProjectMember,
  ProjectRole,
  UserProject,
  Uuid,
} from "@/types"

export type MembersListResponse = {
  members: ProjectMember[]
  total: number
}

type ProjectUpdates = {
  name?: string
}

export type CreateProjectRequest = {
  name: string
  plan: BackendBillingPlanName
  billing_name: string
  billing_email: string
  promo_code_id?: string
  payment_method?: string
  // TODO - not yet supported on the backend
  // template_source?: string
}

export type ProjectRoleRes = {
  role: ProjectRole
}

// Define a service using a base URL and expected endpoints
export const projectsApi = createApi({
  reducerPath: "projectsApi",
  tagTypes: [Tag.MyProjects, Tag.ProjectMembers, Tag.MyProjectRole],
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}projects`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    projectsPost: build.mutation<Project, CreateProjectRequest>({
      query: (body) => ({
        url: "",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tag.MyProjects],
    }),
    projectsGet: build.query<Project, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: project_id,
      }),
    }),
    projectsGetByName: build.query<
      Project,
      { username: string; project_name: string }
    >({
      query: (params) => ({
        url: "",
        params,
      }),
    }),
    projectsPatch: build.mutation<
      Project,
      ProjectIdParam & { updates: ProjectUpdates }
    >({
      query: ({ project_id, updates }) => ({
        url: project_id,
        method: "PATCH",
        body: updates,
      }),
    }),
    projectsDelete: build.mutation<void, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: project_id,
        method: "DELETE",
      }),
      invalidatesTags: [Tag.MyProjects],
    }),
    projectsGetMyProjects: build.query<UserProject[], void>({
      query: () => ({
        url: "my-projects",
      }),
      providesTags: [Tag.MyProjects],
    }),
    projectsGetMyRole: build.query<ProjectRoleRes, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `${project_id}/my-role`,
      }),
      providesTags: (_result, _error, { project_id }) => [
        { type: Tag.MyProjectRole, id: project_id },
      ],
    }),
    projectsGetMembers: build.query<
      MembersListResponse,
      ProjectIdParam & Pagination
    >({
      query: ({ project_id, offset, limit }) => ({
        url: `${project_id}/members?offset=${offset}&limit=${limit}`,
      }),
      providesTags: (_result, _error, { project_id }) => [
        { type: Tag.ProjectMembers, id: project_id },
      ],
    }),
    projectsUpdateMemberRole: build.mutation<
      void,
      ProjectIdParam & { user_id: Uuid; role: ProjectRole }
    >({
      query: ({ project_id, user_id, role }) => ({
        url: `${project_id}/members/${user_id}`,
        method: "PATCH",
        body: { role },
      }),
      // An admin can demote themselves, which changes what the settings page
      // lets them do, so the viewer's own role is invalidated too.
      invalidatesTags: (_result, _error, { project_id }) => [
        { type: Tag.ProjectMembers, id: project_id },
        { type: Tag.MyProjectRole, id: project_id },
      ],
    }),
    projectsDeleteMembers: build.mutation<
      void,
      ProjectIdParam & { user_id: Uuid }
    >({
      query: ({ project_id, user_id }) => ({
        url: `${project_id}/members`,
        method: "DELETE",
        mode: "cors",
        credentials: "include",
        body: [user_id],
      }),
      invalidatesTags: (_result, _error, { project_id }) => [
        { type: Tag.ProjectMembers, id: project_id },
      ],
    }),
  }),
})

export const {
  useProjectsGetQuery,
  useProjectsGetByNameQuery,
  useProjectsPostMutation,
  useProjectsPatchMutation,
  useProjectsDeleteMutation,
  useProjectsGetMembersQuery,
  useProjectsUpdateMemberRoleMutation,
  useProjectsDeleteMembersMutation,

  useProjectsGetMyProjectsQuery,
  useProjectsGetMyRoleQuery,
} = projectsApi
